/**
 * TileMapLoader.ts
 * Parses a Tiled JSON (.tmj) map into engine-ready level data.
 *
 * Reads Terrain tile layer and classifies tiles as solid ground or one-way
 * platforms using hard-coded tile ID sets (GROUND_TILE_IDS / PLATFORM_TILE_IDS
 * from tileMapHelpers). Adjacent solid tiles are merged into single wide AABBs
 * via run-length encoding for collision performance.
 *
 * Also reads the Entities object layer for the player spawn and enemy spawns,
 * and the Collectibles layer for star seeds / constellation shards / checkpoints.
 * `exit` objects on the Entities layer are authoring markers only — nothing in
 * the runtime reads them.
 *
 * DECISION: Solid geometry derived from Terrain tile layer (not objects).
 * Adjacent solid tiles merged into single wide AABBs for collision perf.
 *
 * @module TileMapLoader
 */

import type { WorldId } from '../../constants/gameConstants';
import Logger from '../utils/logger';
import type {
  LevelData,
  PlatformData,
  HazardData,
  CheckpointData,
  StarSeedData,
  ConstellationShardData,
  HeartFragmentData,
  WeaponPickupData,
} from '../types/level';
import type { LevelRuntime } from '../engine/gameState';
import type { Checkpoint } from './CheckpointSystem';
import type {
  TiledTileLayer,
  TiledObjectGroup,
  TiledMap,
} from '../rendering/TileMapTypes';
import {
  GROUND_TILE_IDS,
  PLATFORM_TILE_IDS,
  PLATFORM_COLLISION_HEIGHT,
  SPIKE_UP_IDS,
  SPIKE_DOWN_IDS,
  STAR_SEED_ID,
  CONSTELLATION_SHARD_ID,
  HEART_FRAGMENT_ID,
  CHECKPOINT_INACTIVE_ID,
  WEAPON_PICKUP_TILE_IDS,
  getProp,
  isTileLayer,
  isObjectGroup,
  extractHorizontalRuns,
} from './tileMapHelpers';
import { parseEnemySpawns, type EnemySpawnData } from './enemySpawnParser';

// Re-export EnemySpawnData so existing `import { EnemySpawnData } from
// '../levels/TileMapLoader'` call sites keep working after the split.
export type { EnemySpawnData };

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Result of `loadTileMap` — everything the game needs for one level session.
 */
export interface TileMapResult {
  /** Full level data for save/load and collectible tracking. */
  levelData: LevelData;
  /** Minimal level geometry for the physics engine. */
  levelRuntime: LevelRuntime;
  /**
   * Checkpoint definitions ready for CheckpointSystem.
   * The spawn position for each checkpoint is the tile directly to the right
   * of the checkpoint tile (so Nova lands at the checkpoint post).
   */
  checkpoints: Checkpoint[];
  /** Enemy spawn positions parsed from the Entities object layer. */
  enemySpawns: EnemySpawnData[];
  /**
   * Raw Tiled map data — retained so TileRenderer can draw tiles without
   * re-parsing the file. Only the layer data arrays are kept; metadata is
   * already decoded into levelData.
   */
  tiledMap: TiledMap;
}

/**
 * Parse a Tiled JSON map object into engine-ready level data.
 *
 * @param rawJson  - Parsed JSON from the .tmj file (use `require()` or JSON.parse).
 * @param worldId  - The world this level belongs to.
 * @param levelId  - The level number within the world (1–4 for normal, 0 for secret).
 * @param isSecret - True for secret/bonus stages.
 */
export function loadTileMap(
  rawJson: unknown,
  worldId: WorldId,
  levelId: number,
  isSecret = false,
): TileMapResult {
  const map = rawJson as TiledMap;
  const { width: mapW, height: mapH, tilewidth: tW, tileheight: tH } = map;
  const widthPx = mapW * tW;
  const heightPx = mapH * tH;

  // Locate layers by name
  const terrainLayer = map.layers.find(
    l => isTileLayer(l) && l.name === 'Terrain',
  ) as TiledTileLayer | undefined;
  const collectiblesLayer = map.layers.find(
    l => isTileLayer(l) && l.name === 'Collectibles',
  ) as TiledTileLayer | undefined;
  const hazardsLayer = map.layers.find(
    l => isTileLayer(l) && l.name === 'Hazards',
  ) as TiledTileLayer | undefined;
  const entitiesLayer = map.layers.find(
    l => isObjectGroup(l) && l.name === 'Entities',
  ) as TiledObjectGroup | undefined;

  // ── Terrain → platform AABBs ──────────────────────────────────────────────
  // DECISION: Ground tiles use full tile-height collision. Platform tiles use
  // thin collision (top 6px) so Nova can walk underneath them.
  const groundAABBs = terrainLayer
    ? extractHorizontalRuns(terrainLayer.data, mapW, mapH, tW, tH, GROUND_TILE_IDS)
    : [];
  const platformAABBs = terrainLayer
    ? extractHorizontalRuns(terrainLayer.data, mapW, mapH, tW, tH, PLATFORM_TILE_IDS).map(
        aabb => ({ ...aabb, height: PLATFORM_COLLISION_HEIGHT }),
      )
    : [];
  const rawPlatforms: PlatformData[] = [
    ...groundAABBs.map(aabb => ({ hitbox: aabb, passThrough: false })),
    ...platformAABBs.map(aabb => ({ hitbox: aabb, passThrough: true })),
  ];

  // ── Hazards → spike AABBs ─────────────────────────────────────────────────
  const hazards: HazardData[] = [];
  if (hazardsLayer) {
    const spikeAABBs = [
      ...extractHorizontalRuns(hazardsLayer.data, mapW, mapH, tW, tH, SPIKE_UP_IDS),
      ...extractHorizontalRuns(hazardsLayer.data, mapW, mapH, tW, tH, SPIKE_DOWN_IDS),
    ];
    for (const aabb of spikeAABBs) {
      hazards.push({ hitbox: aabb });
    }
  }

  // ── Collectibles → seeds, shards, heart fragments, weapon pickups, checkpoints
  const starSeeds: StarSeedData[] = [];
  const constellationShards: ConstellationShardData[] = [];
  const heartFragments: HeartFragmentData[] = [];
  const weaponPickups: WeaponPickupData[] = [];
  const checkpointDataList: CheckpointData[] = [];
  const checkpoints: Checkpoint[] = [];

  if (collectiblesLayer) {
    const { data } = collectiblesLayer;
    for (let i = 0; i < data.length; i++) {
      const tileId = data[i];
      if (tileId === 0) continue;
      const col = i % mapW;
      const row = Math.floor(i / mapW);
      const px = col * tW;
      const py = row * tH;

      if (tileId === STAR_SEED_ID) {
        const id = `seed-${i}`;
        starSeeds.push({ id, x: px, y: py });
      } else if (tileId === CONSTELLATION_SHARD_ID) {
        const id = `shard-${i}`;
        constellationShards.push({ id, x: px, y: py });
      } else if (tileId === HEART_FRAGMENT_ID) {
        const id = `hfrag-${i}`;
        heartFragments.push({ id, x: px, y: py });
      } else if (WEAPON_PICKUP_TILE_IDS.has(tileId)) {
        // DECISION: Single Map.get() routes the tile straight to its
        // WeaponPickupKind — adding a new pickup variant only requires
        // editing WEAPON_PICKUP_TILE_IDS, not this branch.
        const kind = WEAPON_PICKUP_TILE_IDS.get(tileId)!;
        const id = `wp-${i}`;
        weaponPickups.push({ id, kind, x: px, y: py });
      } else if (tileId === CHECKPOINT_INACTIVE_ID) {
        const id = `cp-${col}-${row}`;
        checkpointDataList.push({ id, x: px, y: py });
        // Checkpoint trigger zone: a 32×48 area covering the post tile plus
        // one tile of horizontal overlap so Nova reliably activates it.
        checkpoints.push({
          // DECISION: Size the trigger from the map's own tile dimensions
          // rather than the global TILE_SIZE constant. Both are 16 in every
          // shipped level, so this is currently an identity change — but
          // mixing the two means a map authored at a different tile size
          // would silently get a mis-sized trigger zone.
          bounds: { x: px - tW, y: py - tH * 2, width: tW * 3, height: tH * 4 },
          id,
          spawnX: px,
          spawnY: py - tH * 3, // place Nova's top-left above ground surface
        });
      }
    }
  }

  // ── Entity objects → player spawn ─────────────────────────────────────────
  // NOTE: `exit` objects also live on this layer, but the runtime does not read
  // them — level completion is derived from Nova reaching the right edge (see
  // bossArena.ts). They are authoring markers only; do not add behaviour that
  // depends on them without wiring the completion check to match.
  let playerSpawnX = tW * 2;   // fallback: two tiles from left
  let playerSpawnY = heightPx - tH * 4;
  if (entitiesLayer) {
    const spawnObj = entitiesLayer.objects.find(o => o.type === 'player_spawn');
    if (spawnObj) {
      playerSpawnX = spawnObj.x;
      playerSpawnY = spawnObj.y;
    } else {
      Logger.warn(
        'LevelLoader',
        `Level ${worldId}-${levelId} has no player_spawn object; falling back to (${playerSpawnX}, ${playerSpawnY}).`,
      );
    }
  }
  // Enemy spawn objects are parsed by a dedicated helper so the
  // Tiled-type → EnemySpawnData dispatch table can grow without
  // pushing this file past the AGENTS.md 300-line cap.
  const enemySpawns: EnemySpawnData[] = parseEnemySpawns(entitiesLayer);

  // ── Level name ────────────────────────────────────────────────────────────
  const levelName = getProp<string>(map.properties, 'level_name', `World ${worldId}-${levelId}`);

  // ── Assemble LevelData ────────────────────────────────────────────────────
  const levelData: LevelData = {
    worldId,
    levelId,
    name: levelName,
    widthPx,
    heightPx,
    playerSpawnX,
    playerSpawnY,
    platforms: rawPlatforms,
    hazards,
    checkpoints: checkpointDataList,
    starSeeds,
    constellationShards,
    heartFragments,
    weaponPickups,
    isSecret,
  };

  // ── Assemble LevelRuntime (physics engine format) ─────────────────────────
  // DECISION: Separate solid and one-way platforms so the collision resolver
  // can apply pass-through logic to floating platforms only.
  const solidPlatforms = rawPlatforms.filter(p => !p.passThrough).map(p => p.hitbox);
  const oneWayPlatforms = rawPlatforms.filter(p => p.passThrough).map(p => p.hitbox);

  const levelRuntime: LevelRuntime = {
    width: widthPx,
    height: heightPx,
    platforms: solidPlatforms,
    oneWayPlatforms,
    // DECISION: Carry the parsed spikes onto the runtime too. `LevelData` is
    // the editor-facing shape and the engine never reads it, so hazards that
    // stop here are invisible to gameplay.
    hazards: hazards.map(h => h.hitbox),
  };

  return {
    levelData,
    levelRuntime,
    checkpoints,
    enemySpawns,
    tiledMap: map,
  };
}
